import React, {ComponentType, useEffect, useState} from "react";
import EditBookingFooter from "./index";
import {IContainerProps, IHandlers, IProps, IServices} from './types';

/**
 * Gets the interface types as array of strings so we don't need hand code them each time (and reduces chance for mistakes).
 */
import {keys} from 'ts-transformer-keys';
import {hot} from "react-hot-loader";
import ReactUtilsService from "app/services/reactUtils.service";
import {BookingMethods, BookingType, IBooking} from "app/app.types";
import {Environment} from "appRoot/app.constants";
import AdminService from "external-services/diary-admin-service/admin.service";
import ManagerService from "external-services/manager-service/manager.service";
import BookingDragService from 'app/services/bookingDrag/bookingDrag.service';
import ScheduleLookupService from 'external-services/schedule-lookup-service/scheduleLookup.service';
import { BookingStatusNames, IDiarySchedule } from 'shared-types/index';
import ServiceDetectionService from "external-services/service-detection-service/serviceDetection.service";
import StatusConfigService from 'external-services/status-config-service';
import {Provider, useSelector} from "react-redux";
import {DiaryRootState} from "appRoot/rootReducer";
import store from "appRoot/Store";

const NS = 'EditBookingFooterContainer';

const propsKeys: string[] = keys<IProps>();
const containerPropsKeys: string[] = keys<IContainerProps>();
const handlersKeys: string[] = keys<IHandlers>();
const servicesKeys: string[] = keys<IServices>();

const ThemeWrap = function(all: IProps & IContainerProps & IHandlers & IServices) {

  const fullStatusOptions = useSelector(
    (state: DiaryRootState) => state.statusReducer.fullStatusOptions
  );

  /**
    * Sorts 'all' into IServices, IProps and IContainerProps
    */
  const {
    EditBookingActions, appConstants
  }: IServices = ReactUtilsService.getObjectByKeys(all, servicesKeys) as IServices;
  const props: IProps = ReactUtilsService.getObjectByKeys(all, propsKeys) as IProps;
  const {
    booking, customer, customerLoaded, serviceId, selectedTime, isSaveEnabled, isLockedBooking,
    onSave, onSendSms, onClose, onResendConfirmationUpdate, onResendSmsConfirmationUpdate, paxThresholdError, disableNotification
  }: IContainerProps = ReactUtilsService.getObjectByKeys(all, containerPropsKeys) as IContainerProps;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>(null);
  props.customer = customer;
  props.isManagerAndLocalEnv = ManagerService.isManager && Environment.IsLocal;
  props.bookingStatusName = booking.status.name;
  props.bookingStatusCode = booking.status.code;
  props.showDateChange = booking.bookingType !== BookingType.WaitList;
  props.errorMsg = errorMsg;
  props.isLoading = isLoading || !customerLoaded;
  props.isStandbyOrWaitlist = booking.bookingType === BookingType.StandbyList || booking.bookingType === BookingType.WaitList;
  props.paxThresholdError = paxThresholdError;
  props.isSmsEnabled = AdminService.getVenue().smsEnabled;
  props.disableNotification = disableNotification;
  props.isLockedBooking = isLockedBooking;
  const isBookingValid = booking.bookingType === BookingType.StandbyList ||
    booking.bookingType === BookingType.WaitList && booking.method === BookingMethods.walkin ||
    !!(booking.tables && booking.tables.length) || booking.method === BookingMethods.walkin;

  const [checkStickyService, setCheckStickyService] = useState<boolean>(true); // we allow stickiness to be checked at the start
  const [isServiceSticky, setIsServiceSticky] = useState<boolean>(false); // but stickiness is false until 'ServiceDetectionService.hasSectionsInService' called

  // stores original values so we know if there has been a change, which would override sticky logic
  const [originalSectionId, setOriginalSectionId] = useState<string>(null);
  const [originalServiceId, setOriginalServiceId] = useState<string>(null);
  const [originalTime, setOriginalTime] = useState<Date>(null);

  useEffect(() => {
    if (!originalTime && selectedTime) {
      setOriginalTime(selectedTime.time);
    }
  }, [selectedTime]);

  useEffect(() => {
    if (!originalServiceId && serviceId) {
      setOriginalServiceId(serviceId);
    }
  }, [serviceId]);

  useEffect(() => {
    if (!originalSectionId && booking.preferredSectionId) {
      setOriginalSectionId(booking.preferredSectionId);
    }
  }, [booking.preferredSectionId]);

  /**
   * If the booking came from a service via a drag or swap it may have retained the serviceId of its previous service, despite it being
   * moved to a table in a different section. This is a special "sticky service" scenario for dragging/swapping bookings within the timeline
   * and we don't want it to cause the save button to be disabled.
   */
  props.isSaveEnabled =AdminService.isPartnerBookingsLocked(booking?.partner) === true ? false : (
    isServiceSticky ? true : isBookingValid
    );
  // this is for data validation, which make save button still can click, but looks like disable, for jump to invalid tab
  props.tabValidationError = !isSaveEnabled;
  useEffect(() => {
    if (!checkStickyService) { // stickiness check has been blocked due to a user change to service, section or time
      return;
    }
    ScheduleLookupService.getSchedule(selectedTime?.time).then((schedule: IDiarySchedule) => {
      const hasSectionsInService = ServiceDetectionService.hasSectionsInService(serviceId, booking.preferredSectionId, schedule?.serviceLayouts);
      if (!hasSectionsInService) {
        setIsServiceSticky(true); // service becomes sticky
      }
    });
  }, [checkStickyService]);


  useEffect(() => {
    if (!checkStickyService || !isServiceSticky) { // stickiness check has been blocked due to a user change to service, section or time
      return;
    }

    let didChange = false;
    if (originalSectionId && booking.preferredSectionId !== originalSectionId) { // section changed
      setOriginalSectionId(booking.preferredSectionId);
      didChange = true;
    }
    if (originalServiceId && serviceId !== originalServiceId) { // service changed
      setOriginalServiceId(serviceId);
      didChange = true;
    }
    if (originalTime && (selectedTime.time !== originalTime)) { // time changed
      setOriginalTime(selectedTime.time);
      didChange = true;
    }
    if (didChange) {
      setCheckStickyService(false);
      setIsServiceSticky(false);
    }
  }, [booking.preferredSectionId, serviceId, selectedTime, checkStickyService]); // if service or section or time are changed we ditch the sticky service logic

  /**
   * Handlers used to modify app state (via a service) from within the module's container.
   * Similar to redux mapDispatchToProps.
   */
  const handlers: IHandlers = {
    handleSaveClick: () => {
      setErrorMsg(null);
      onSave();
    },
    handleCloseClick: () => {
      setErrorMsg(null);
      onClose();
    },
    handleDeleteConfirmed: () => {
      setErrorMsg(null);
      setIsLoading(true);
      EditBookingActions.deleteBookingConfirmed(booking)
        .then((booking: IBooking | boolean) => {
          setIsLoading(false);
        //  @todo: close panel?
        })
        .catch((err: string) => {
          setIsLoading(false);
          setErrorMsg('Sorry, there was a problem deleting this booking.');
        });
    },
    handleDeleteCancelled: () => {
      setErrorMsg(null);
      EditBookingActions.deleteBookingCancelled(booking);
    },
    handleDateChangeClick:() => {
      setErrorMsg(null);
      EditBookingActions.changeBookingDate(booking, customer);
    },
    handleSendSMSClick: () => {
      setErrorMsg(null);
      onSendSms();
    },
    handleResendConfirmationUpdate: (sendConfirmation: boolean) => {
      onResendConfirmationUpdate(sendConfirmation);
    },
    handleResendSMSConfirmationUpdate: (sendSmsConfirmation: boolean) => {
      onResendSmsConfirmationUpdate(sendSmsConfirmation);
    },
    isStatusNameInList: (statusName: string, types: string[]) => {
      // if the status is No Show, we don't want to display the send email checkbox
      if (statusName === BookingStatusNames.CancelledNoShow) {
        return false;
      }
      return StatusConfigService.checkStatusOptions(statusName, types, fullStatusOptions);
    }
  }

  return ReactUtilsService.containerWrap(
    <EditBookingFooter {...props} {...handlers} />
  );
}

const StoreWrap = function (props: IProps & IContainerProps & IHandlers & IServices) {
  return (
    <Provider store={store}>
      <ThemeWrap {...props} />
    </Provider>
  )
}

/**
 * Returns properties needed for rendering react2angular
 */
export const R2AEditBookingFooter: [ComponentType<unknown>, never[]?, string[]?] = [
  hot(module)(StoreWrap) as ComponentType<unknown>,
  [...containerPropsKeys] as never[],
  servicesKeys
];
